// src/lib/i18n/t.ts
// Les textes fixes de l'interface, dans la langue de la page.
//
// ── Le parti pris (11/10/2026) ─────────────────────────────────────────────────────────
//
// Les dictionnaires sont des DONNÉES, pas du code : une chaîne par clé, jamais une fonction.
// C'est ce qui permettra à la page d'administration des textes de les surcharger depuis la
// base sans redéploiement — une fonction ne se stocke pas dans une colonne.
//
// Deux mécanismes seulement, assez pour une interface et assez simples pour qu'un relecteur
// les manie sans documentation :
//
//   « {nom} »                     une variable : t('bs.page.engagementsDe', { nom: 'Valls' })
//   { one: '…', other: '…' }      un pluriel, choisi sur la variable `n` selon les règles de
//                                 la langue (Intl.PluralRules) : en français 0 et 1 sont au
//                                 singulier, en anglais 0 est au pluriel. `zero` est accepté
//                                 quand une langue veut dire « aucun » plutôt que « 0 ».
//
// Le français est la langue de référence : son dictionnaire définit les clés, et l'anglais
// et l'espagnol doivent les porter toutes (le typage le vérifie à la compilation, les tests
// vérifient que les variables sont les mêmes). Une clé absente d'une surcharge retombe sur
// le dictionnaire, jamais sur un trou.
//
// Rien ici n'importe Next ni React : le module sert au serveur, au client, au PDF et aux
// scripts.

import type { Locale } from './config';
import { fr } from './messages/fr';
import { en } from './messages/en';
import { es } from './messages/es';

/** Un pluriel : la forme est choisie sur `n`. */
export interface Pluriel {
  zero?: string;
  one: string;
  other: string;
}

export type Message = string | Pluriel;

/** La forme d'un dictionnaire : celle du français, feuilles élargies à `Message`. */
export type Catalogue<T = typeof fr> = {
  [K in keyof T]: T[K] extends string ? string
    : T[K] extends { one: string; other: string } ? Pluriel
    : Catalogue<T[K]>;
};

type Chemins<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string | { one: string; other: string }
    ? `${P}${K}`
    : Chemins<T[K], `${P}${K}.`>;
}[keyof T & string];

/** Toutes les clés du dictionnaire, en notation pointée : 'bs.onglets.verdict'. */
export type Cle = Chemins<typeof fr>;

export type Variables = Record<string, string | number | null | undefined>;
export type T = (cle: Cle, vars?: Variables) => string;

export const DICTIONNAIRES: Record<Locale, Catalogue> = { fr, en, es };

/** Les surcharges posées en administration : clé pointée → texte. Vide tant qu'il n'y en a pas. */
export type Surcharges = Partial<Record<string, Message>>;

function lire(catalogue: unknown, cle: string): Message | undefined {
  let n: unknown = catalogue;
  for (const morceau of cle.split('.')) {
    if (n === null || typeof n !== 'object') return undefined;
    n = (n as Record<string, unknown>)[morceau];
  }
  if (typeof n === 'string') return n;
  // Un pluriel porte « one » ET « other » : une table de libellés peut avoir une clé « other ».
  if (n && typeof n === 'object' && typeof (n as Pluriel).one === 'string' && typeof (n as Pluriel).other === 'string') return n as Pluriel;
  return undefined;
}

const reglesDePluriel = new Map<Locale, Intl.PluralRules>();
function formeDuPluriel(lang: Locale, m: Pluriel, n: number): string {
  if (n === 0 && m.zero !== undefined) return m.zero;
  let regles = reglesDePluriel.get(lang);
  if (!regles) reglesDePluriel.set(lang, (regles = new Intl.PluralRules(lang)));
  return regles.select(n) === 'one' ? m.one : m.other;
}

/** Remplace « {nom} » par sa valeur. Une variable non fournie reste visible : un trou se voit. */
export function interpoler(gabarit: string, vars?: Variables): string {
  if (!vars) return gabarit;
  return gabarit.replace(/\{(\w+)\}/g, (tout, nom: string) => {
    const v = vars[nom];
    return v === null || v === undefined ? tout : String(v);
  });
}

/**
 * La fonction `t` d'une langue. Ordre de recherche : surcharge d'administration, dictionnaire
 * de la langue, dictionnaire français, la clé elle-même (visible, donc corrigée).
 */
export function creerT(lang: Locale, surcharges?: Surcharges): T {
  return (cle, vars) => {
    const m = surcharges?.[cle] ?? lire(DICTIONNAIRES[lang], cle) ?? lire(fr, cle);
    if (m === undefined) return cle;
    if (typeof m === 'string') return interpoler(m, vars);
    const n = Number(vars?.n);
    return interpoler(formeDuPluriel(lang, m, Number.isFinite(n) ? n : 0), vars);
  };
}

// ── Les nombres ────────────────────────────────────────────────────────────────────────
//
// « 6,2 » en français et en espagnol, « 6.2 » en anglais. Les pages écrivaient
// `toFixed(1)` — le point anglais servi au lecteur français — ou `.replace('.', ',')` au cas
// par cas. Une seule fonction, et la langue décide.

const formats = new Map<string, Intl.NumberFormat>();

/** Un nombre à `decimales` chiffres après la virgule (fixes), dans la langue. */
export function nombre(lang: Locale, valeur: number, decimales = 0): string {
  const cle = `${lang}:${decimales}`;
  let f = formats.get(cle);
  if (!f) {
    f = new Intl.NumberFormat(lang, { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
    formats.set(cle, f);
  }
  return f.format(valeur);
}

// ── Les dates ──────────────────────────────────────────────────────────────────────────
//
// `toLocaleDateString("fr-FR", …)` écrit en dur dans les pages servait le français à tout le
// monde. Ces deux formes couvrent l'essentiel ; une date absente ou illisible rend ''.

function lireDate(d: string | number | Date | null | undefined): Date | null {
  if (d === null || d === undefined || d === '') return null;
  // « AAAA-MM-JJ » seul est lu en heure locale : en UTC, le 5 octobre deviendrait le 4 à l'ouest.
  const x = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`) : new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

/** « 5 oct. » / « Oct 5 » / « 5 oct ». */
export function dateCourte(lang: Locale, d: string | number | Date | null | undefined): string {
  const x = lireDate(d);
  return x ? x.toLocaleDateString(lang, { day: 'numeric', month: 'short' }) : '';
}

/** « 5 octobre 2026 » / « October 5, 2026 » / « 5 de octubre de 2026 ». */
export function dateLongue(lang: Locale, d: string | number | Date | null | undefined): string {
  const x = lireDate(d);
  return x ? x.toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

/** « 6,2 », « 7 » : une décimale au plus, et pas de « ,0 » (l'ancien `virgule` des signes). */
export function nombreCourt(lang: Locale, valeur: number): string {
  const v = Math.round(valeur * 10) / 10;
  return nombre(lang, v, Number.isInteger(v) ? 0 : 1);
}
